import React, { useState, useEffect } from "react";
import { Package, Plus, Edit, Trash2, Save, X } from "feather-icons-react/build/IconComponents";
import { base_api_url } from "../../../environment.jsx";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import PropTypes from 'prop-types';

const ArticleComposition = ({ show, onClose, articleData, onCompositionUpdate }) => {
  const MySwal = withReactContent(Swal);
  const [loading, setLoading] = useState(false);
  const [compositions, setCompositions] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [editingComposition, setEditingComposition] = useState(null);
  const [newComposition, setNewComposition] = useState({
    id_ingredient: '',
    proportion: ''
  });
  const [showAddForm, setShowAddForm] = useState(false);

  // Charger les données initiales
  useEffect(() => {
    if (show && articleData?.id_article) {
      fetchCompositions();
      fetchIngredients();
    }
  }, [show, articleData]);

  // Récupérer les compositions existantes
  const fetchCompositions = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.access_token;
      const response = await axios.get(
        `${base_api_url}api/composition/${articleData.id_article}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        setCompositions(response.data.composition || []);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des compositions:", error);
      if (error.response?.status !== 404) {
        showErrorMessage("Impossible de charger les compositions de l'article.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Récupérer les ingrédients disponibles
  const fetchIngredients = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.access_token;
      const ideresto = JSON.parse(localStorage.getItem("user"))?.restoId;
      const response = await axios.get(
        `${base_api_url}api/ingredientRest/${ideresto}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        setIngredients(response.data.ingredients || response.data.ingredient || []);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des ingrédients:", error);
      showErrorMessage("Impossible de charger les ingrédients.");
    }
  };

  // Ajouter une nouvelle composition
  const handleAddComposition = async (e) => {
    e.preventDefault();
    
    if (!newComposition.id_ingredient || !newComposition.proportion) {
      showErrorMessage("Veuillez sélectionner un ingrédient et saisir une proportion.");
      return;
    }

    // Vérifier si l'ingrédient n'est pas déjà utilisé
    const existingComposition = compositions.find(
      comp => comp.id_ingredient.toString() === newComposition.id_ingredient
    );
    
    if (existingComposition) {
      showErrorMessage("Cet ingrédient est déjà utilisé dans cette composition.");
      return;
    }



    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.access_token;
      const payload = {
        ingredient: [{
          id_ingredient: parseInt(newComposition.id_ingredient),
          proportion: parseFloat(newComposition.proportion)
        }]
      };

      const response = await axios.post(
        `${base_api_url}api/composition/${articleData.id_article}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200 || response.status === 201) {
        showSuccessMessage("Ingrédient ajouté à la composition avec succès !");
        setNewComposition({ id_ingredient: '', proportion: '' });
        setShowAddForm(false);
        await fetchCompositions(); // Recharger les compositions
        onCompositionUpdate && onCompositionUpdate();
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout de la composition:", error);
      handleApiError(error, "Impossible d'ajouter l'ingrédient à la composition.");
    } finally {
      setLoading(false);
    }
  };

  // Modifier une composition existante
  const handleUpdateComposition = async (compositionId, updatedData) => {


    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.access_token;
      const response = await axios.put(
        `${base_api_url}api/composition/${compositionId}`,
        updatedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        showSuccessMessage("Composition mise à jour avec succès !");
        setEditingComposition(null);
        await fetchCompositions(); // Recharger les compositions
        onCompositionUpdate && onCompositionUpdate();
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la composition:", error);
      handleApiError(error, "Impossible de mettre à jour la composition.");
    } finally {
      setLoading(false);
    }
  };

  // Supprimer une composition (mettre le statut à 0)
  const handleDeleteComposition = async (compositionId) => {
    const result = await MySwal.fire({
      // title: 'Confirmer la suppression',
      text: "Êtes-vous sûr de vouloir supprimer cet ingrédient de la composition ?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      await handleUpdateComposition(compositionId, { statut: 0 });
    }
  };

  // Obtenir le nom d'un ingrédient par son ID
  const getIngredientName = (ingredientId) => {
    const ingredient = ingredients.find(ing => ing.id_ingredient === ingredientId);
    return ingredient ? `${ingredient.nom} (${ingredient.code})` : `Ingrédient ${ingredientId}`;
  };

  // Obtenir les ingrédients disponibles (non encore utilisés)
  const getAvailableIngredients = () => {
    const usedIngredientIds = compositions
      .filter(comp => comp.statut === 1)
      .map(comp => comp.id_ingredient);
    
    return ingredients.filter(ing => !usedIngredientIds.includes(ing.id_ingredient));
  };

  // Messages d'erreur et de succès
  const showSuccessMessage = (message) => {
    MySwal.fire({
      icon: "success",
      // title: "Succès !",
      text: message,
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      customClass: { popup: "pixel-toast" },
    });
  };

  const showErrorMessage = (message) => {
    MySwal.fire({
      icon: "error",
      // title: "Erreur",
      text: message,
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      customClass: { popup: "pixel-toast" },
    });
  };

  const handleApiError = (error, defaultMessage) => {
    let errorMessage = defaultMessage;
    
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.status === 401) {
      errorMessage = "Session expirée. Veuillez vous reconnecter.";
    } else if (error.response?.status === 404) {
      errorMessage = "Ressource non trouvée.";
    }
    
    showErrorMessage(errorMessage);
  };

  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <Package size={20} className="me-2" />
              Composition - {articleData?.nom}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={loading}
            />
          </div>

          <div className="modal-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Chargement...</span>
                </div>
                <p className="mt-2 text-muted">Chargement des compositions...</p>
              </div>
            ) : (
              <>
                {/* En-tête avec statistiques */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card bg-light">
                      <div className="card-body py-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">Ingrédients utilisés</small>
                            <h6 className="mb-0">{compositions.filter(c => c.statut === 1).length}</h6>
                          </div>
                          
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => setShowAddForm(!showAddForm)}
                            disabled={getAvailableIngredients().length === 0}
                          >
                            <Plus size={16} className="me-1" />
                            Ajouter
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Formulaire d'ajout */}
                {showAddForm && (
                  <div className="card border-primary mb-3">
                    <div className="card-header bg-primary text-white">
                      <h6 className="mb-0">Ajouter un ingrédient</h6>
                    </div>
                    <div className="card-body">
                      <form onSubmit={handleAddComposition}>
                        <div className="row">
                          <div className="col-md-6 col-12 mb-3">
                            <label className="form-label">Ingrédient *</label>
                            <select
                              className="form-control"
                              value={newComposition.id_ingredient}
                              onChange={(e) =>
                                setNewComposition({
                                  ...newComposition,
                                  id_ingredient: e.target.value
                                })
                              }
                              required
                            >
                              <option value="">Sélectionner un ingrédient</option>
                              {getAvailableIngredients().map(ingredient => (
                                <option 
                                  key={ingredient.id_ingredient} 
                                  value={ingredient.id_ingredient}
                                >
                                  {ingredient.nom} ({ingredient.code})
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-4 col-8 mb-3">
                            <label className="form-label">Proportion (%) *</label>
                            <input
                              type="number"
                              className="form-control"
                              min="0.1"
                              max="100"
                              step="0.1"
                              placeholder="Ex: 25.5"
                              value={newComposition.proportion}
                              onChange={(e) =>
                                setNewComposition({
                                  ...newComposition,
                                  proportion: e.target.value
                                })
                              }
                              required
                            />
                          </div>
                          <div className="col-md-2 col-4 mb-3 d-flex align-items-end">
                            <div className="btn-group w-100">
                              <button
                                type="submit"
                                className="btn btn-success btn-sm"
                                disabled={loading}
                              >
                                <Save size={14} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => {
                                  setShowAddForm(false);
                                  setNewComposition({ id_ingredient: '', proportion: '' });
                                }}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Liste des compositions */}
                <div className="compositions-list">
                  {compositions.filter(comp => comp.statut === 1).length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <Package size={48} className="mb-3 opacity-50" />
                      <h6>Aucun ingrédient dans cette composition</h6>
                      <p>Ajoutez des ingrédients pour définir la composition de cet article.</p>
                    </div>
                  ) : (
                    <div className="row">
                      {compositions
                        .filter(comp => comp.statut === 1)
                        .map((composition) => (
                          <div key={composition.id_composition} className="col-12 mb-2">
                            <div className="card">
                              <div className="card-body py-2">
                                {editingComposition === composition.id_composition ? (
                                  // Mode édition
                                  <EditCompositionForm
                                    composition={composition}
                                    ingredients={ingredients}
                                    onSave={(updatedData) =>
                                      handleUpdateComposition(composition.id_composition, updatedData)
                                    }
                                    onCancel={() => setEditingComposition(null)}
                                    loading={loading}
                                  />
                                ) : (
                                  // Mode affichage
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="flex-grow-1">
                                      <div className="d-flex align-items-center">
                                        <div
                                          className="bg-success rounded-circle d-flex align-items-center justify-content-center me-3"
                                          style={{ width: "32px", height: "32px", fontSize: "12px", color: "white" }}
                                        >
                                          {getIngredientName(composition.id_ingredient).charAt(0)}
                                        </div>
                                        <div>
                                          <h6 className="mb-0">{getIngredientName(composition.id_ingredient)}</h6>
                                          <small className="text-muted">
                                            Proportion: {composition.proportion} {ingredients.filter(ing => ing.id_ingredient === composition.id_ingredient)[0]?.unite || ''}
                                            {composition.date_mise_a_jour && (
                                              <> • Modifié le {new Date(composition.date_mise_a_jour).toLocaleDateString()}</>
                                            )}
                                          </small>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="btn-group btn-group-sm">
                                      <button
                                        className="btn btn-outline-primary"
                                        onClick={() => setEditingComposition(composition.id_composition)}
                                        title="Modifier"
                                      >
                                        <Edit size={14} />
                                      </button>
                                      <button
                                        className="btn btn-outline-danger"
                                        onClick={() => handleDeleteComposition(composition.id_composition)}
                                        title="Supprimer"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {/* Message si aucun ingrédient disponible */}
                {getAvailableIngredients().length === 0 && !showAddForm && (
                  <div className="alert alert-warning mt-3">
                    <small>
                      <strong>Information :</strong> Tous les ingrédients disponibles sont déjà utilisés dans cette composition.
                    </small>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour l'édition inline d'une composition
const EditCompositionForm = ({ composition, ingredients, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    proportion: composition.proportion,
    statut: composition.statut
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const ingredient = ingredients.find(ing => ing.id_ingredient === composition.id_ingredient);

  return (
    <form onSubmit={handleSubmit}>
      <div className="row align-items-center">
        <div className="col-md-6 col-12 mb-2">
          <div className="d-flex align-items-center">
            <div
              className="bg-warning rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: "32px", height: "32px", fontSize: "12px", color: "white" }}
            >
              <Edit size={14} />
            </div>
            <div>
              <h6 className="mb-0">{ingredient?.nom} ({ingredient?.code})</h6>
              <small className="text-muted">Modification en cours...</small>
            </div>
          </div>
        </div>
        <div className="col-md-4 col-8 mb-2">
          <input
            type="number"
            className="form-control form-control-sm"
            min="0.1"
            max="100"
            step="0.1"
            value={formData.proportion}
            onChange={(e) => setFormData({ ...formData, proportion: parseFloat(e.target.value) })}
            required
          />
        </div>
        <div className="col-md-2 col-4 mb-2">
          <div className="btn-group btn-group-sm w-100">
            <button
              type="submit"
              className="btn btn-success"
              disabled={loading}
            >
              <Save size={14} />
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

ArticleComposition.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  articleData: PropTypes.object,
  onCompositionUpdate: PropTypes.func,
};

EditCompositionForm.propTypes = {
  composition: PropTypes.object.isRequired,
  ingredients: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default ArticleComposition;